import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2026-01-27 08:37:11 +0100 (Tue, 27 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 160043 $
//  Purpose   : showcase components
// =============================================================================
// ---Third-party imports
import React from 'react';
import clsx from 'clsx';
// ---Framework imports
import log from 'uix/logging';
import { useApi, useSelector, useState, useCallback, useRef, useEffect, useInView } from 'uix/hooks';
import { Sel } from 'uix/registries';
import { Button } from 'uix/components';
import { GhostImage, GhostText, PageViewer, Modal } from '.';
import { VIcon } from 'uix/graphics';
// ---Local imports
import S from './showcase.scss';
import { useCaptionLink } from '../hooks';
import { XPopup, AccessWarning } from 'uix/components';
import { PopupContent, PopupHeader } from 'semantic-ui-react';
/// ---Exports
const useImageInfo = (id)=>{
    let w = 1000;
    let imgsrc = `/download/webcache/${w}/${id}`;
    let [isError, setIsError] = useState(false);
    let onError = useCallback(()=>setIsError(true), []);
    return {
        imgsrc,
        onError,
        isError
    };
};
export const Showcase = ({ otid, otids, caption, fontSize, linkid, carousel, imagePosition, imageWidth = '504px', limitText, showFulltext, optionSet, skipFirstPage })=>{
    let apiurl = useSelector(Sel.apiurl);
    let [outerRef, inView] = useInView({
        triggerOnce: true,
        rootMargin: '100%'
    });
    let imgRef = useRef(null);
    let contentRef = useRef(null);
    let [hidden, setHidden] = useState(false);
    let [txtOverflow, setTxtOverflow] = useState(false);
    let [imgHeight, setImgHeight] = useState(false);
    let [imgLoad, setImgLoad] = useState(false);
    let [isFirstLoad, setIsFirstLoad] = useState(true);
    let [currentIndex, setCurrentIndex] = useState(0);
    let [centeredOtid, setCenteredOtid] = useState(undefined) // centered Otid in Modal
    ;
    let [modalOpen, setModalOpen] = useState(false);
    let [popupOpen, setPopupOpen] = useState(false);
    useEffect(()=>{
        const measure = ()=>{
            let imgHeight = imgRef.current?.clientHeight || 0;
            setHidden(false);
            setImgHeight(imgHeight);
            setTxtOverflow(imgHeight < (contentRef.current?.clientHeight || 0) - 5);
        };
        measure();
        let rObserver = new ResizeObserver(measure);
        if (imgRef.current) rObserver.observe(imgRef.current);
        return ()=>rObserver.disconnect();
    }, [
        imgLoad,
        currentIndex
    ]);
    let visible = !!otid && inView;
    let { data, loading: apiLoading } = useApi('presentation/getShowcaseData', {
        otid,
        resolveChildren: !!carousel
    }, {
        condition: visible
    });
    let loading = !data || apiLoading && isFirstLoad;
    const flattenPages = (node, parentText = '', parentTitle = '')=>{
        // bring children and pages on one level
        let currentLevel = [];
        if (node.pages) {
            currentLevel.push(...node.pages.map((p)=>({
                    ...p,
                    text: p.text || node.text || parentText,
                    title: p.title || node.title || parentTitle
                })));
        }
        if (node.children) {
            currentLevel.push(...node.children.map((c)=>({
                    ...c,
                    text: c.text || node.text || parentText,
                    title: c.title || node.title || parentTitle,
                    _isNode: true
                })));
        }
        // sort on first level
        currentLevel.sort((a, b)=>(a.otsort || 0) - (b.otsort || 0));
        // recursion on children (puts parent item.text & .info into children)
        let flattened = [];
        currentLevel.forEach((item)=>{
            if (item._isNode) {
                flattened = flattened.concat(flattenPages(item, item.text, item.title));
            } else {
                flattened.push(item);
            }
        });
        return flattened;
    };
    let allPages = carousel ? data ? flattenPages(data) : [] : data?.pages?.[0] ? [
        {
            ...data.pages[0],
            text: data.text,
            awstate: data.awstate
        }
    ] : [];
    let totalSlides = allPages.length;
    let { imgsrc, onError } = useImageInfo(allPages[currentIndex]?.otid);
    let onGraphicClick = ()=>setModalOpen(true);
    let nextSlide = ()=>setCurrentIndex((prev)=>(prev + 1) % totalSlides);
    let prevSlide = ()=>setCurrentIndex((prev)=>(prev - 1 + totalSlides) % totalSlides);
    let img = /*#__PURE__*/ _jsx(AccessWarning, {
        id: currentIndex,
        awstate: allPages[currentIndex]?.awstate,
        children: /*#__PURE__*/ _jsx("img", {
            onLoad: ()=>{
                setImgLoad(true);
                setIsFirstLoad(false);
            },
            onClick: onGraphicClick,
            src: apiurl.href(imgsrc),
            onError: onError,
            ref: imgRef,
            loading: "lazy"
        })
    });
    let textStyle = null;
    if (!hidden && !loading) {
        textStyle = {
            maxHeight: !limitText ? 'none' : `${imgHeight - 5}px`,
            opacity: imgHeight ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
        };
    }
    let buildMore = ()=>{
        if (!limitText) return null;
        let handleMore = ()=>setHidden(!hidden);
        if (!hidden) {
            return /*#__PURE__*/ _jsxs("a", {
                onClick: handleMore,
                className: S.more,
                children: [
                    "mehr ",
                    /*#__PURE__*/ _jsx(VIcon, {
                        size: "small",
                        name: "arrow down"
                    })
                ]
            });
        }
    };
    let getIdxByOtid = (otid)=>{
        const idx = allPages.findIndex((p)=>p.otid === otid);
        if (idx !== -1) return idx;
    };
    let modalClose = ()=>{
        setCurrentIndex(getIdxByOtid(centeredOtid));
        setModalOpen(false);
    };
    let centeredInfo = null;
    if (centeredOtid) {
        centeredInfo = allPages.find((p)=>p.otid === centeredOtid);
    }
    const addInfoPopup = /*#__PURE__*/ _jsxs(XPopup, {
        basic: true,
        style: {
            opacity: 0.86,
            maxWidth: '66%',
            marginBottom: '2em'
        },
        wide: "very",
        inverted: true,
        size: "small",
        open: popupOpen,
        // onOpen={() => setPopupOpen(true)}
        // positionFixed
        position: "right center",
        trigger: /*#__PURE__*/ _jsx(Button, {
            style: {
                backgroundColor: 'var(--presentation_themeColor)',
                color: 'black'
            },
            inverted: true,
            onClick: ()=>setPopupOpen(!popupOpen),
            icon: popupOpen ? 'close' : 'info'
        }),
        children: [
            /*#__PURE__*/ _jsxs(PopupHeader, {
                children: [
                    centeredInfo?.title,
                    ' ',
                    /*#__PURE__*/ _jsx(VIcon, {
                        style: {
                            display: 'inline-block'
                        },
                        name: "link",
                        href: apiurl.baseUrl('/' + centeredOtid),
                        target: "_blank"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PopupContent, {
                children: centeredInfo?.text
            })
        ]
    });
    let options = optionSet || showFulltext && 'h1' || 'grid';
    let modalContent = /*#__PURE__*/ _jsx(PageViewer, {
        otids: allPages.map((pages)=>pages.otid),
        otid: [
            allPages[currentIndex]?.otid
        ],
        onCenterChange: (otid)=>setCenteredOtid(otid),
        mobile: false,
        optionSet: options,
        navigationControls: true,
        height: "100%",
        zoomControls: "inOut",
        initialZoomMode: "areaFit",
        skipFirstPage: skipFirstPage,
        showFulltext: showFulltext,
        addContent: (area)=>{
            if (!centeredInfo) return null;
            if (area === 'viewer') {
                return /*#__PURE__*/ _jsx("span", {
                    style: {
                        color: 'red',
                        position: 'absolute',
                        bottom: '.3rem',
                        left: '.3rem',
                        zIndex: 20
                    },
                    children: addInfoPopup
                });
            }
        }
    });
    let captionLink = useCaptionLink(caption, linkid, otid, null, true);
    return /*#__PURE__*/ _jsxs("div", {
        className: clsx(S.showcase, imagePosition === 'right' && S.imageRight),
        ref: outerRef,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: clsx(S.content, loading && S.loading),
                children: [
                    carousel && /*#__PURE__*/ _jsxs("div", {
                        className: clsx(S.carouselNav, totalSlides > 1 && S.withSlides),
                        children: [
                            /*#__PURE__*/ _jsx("a", {
                                className: clsx(S.prev, currentIndex <= 0 && S.disabled),
                                onClick: currentIndex > 0 ? ()=>{
                                    setImgLoad(false);
                                    setCurrentIndex(0);
                                } : undefined,
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "chevron_left_end"
                                })
                            }),
                            /*#__PURE__*/ _jsx("a", {
                                className: clsx(S.prev, currentIndex <= 0 && S.disabled),
                                onClick: currentIndex > 0 ? prevSlide : undefined,
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "chevron_left"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    currentIndex + 1,
                                    " / ",
                                    totalSlides
                                ]
                            }),
                            /*#__PURE__*/ _jsx("a", {
                                className: clsx(S.next, currentIndex >= totalSlides - 1 && S.disabled),
                                onClick: currentIndex < totalSlides - 1 ? nextSlide : undefined,
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "chevron_right"
                                })
                            }),
                            /*#__PURE__*/ _jsx("a", {
                                className: clsx(S.next, currentIndex >= totalSlides - 1 && S.disabled),
                                onClick: currentIndex < totalSlides - 1 ? ()=>{
                                    setImgLoad(false);
                                    setCurrentIndex(totalSlides - 1);
                                } : undefined,
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "chevron_right_end"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: clsx(S.imgContainer),
                        style: {
                            '--presenation_showcase_imageWidth': imageWidth
                        },
                        children: [
                            loading && /*#__PURE__*/ _jsx(GhostImage, {
                                width: "100%",
                                height: imgHeight || '320px'
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                style: {
                                    visibility: loading ? 'hidden' : 'visible'
                                },
                                children: allPages[currentIndex]?.otid && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: S.clickable
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            size: "large",
                                            style: {
                                                color: 'var(--presentation_themeColor)',
                                                backgroundColor: 'rgba(0, 0, 0, 0.7)'
                                            },
                                            onClick: onGraphicClick,
                                            className: S.zoomButton,
                                            icon: 'zoom',
                                            circle: true,
                                            inverted: true
                                        }),
                                        img,
                                        captionLink
                                    ]
                                })
                            })
                        ]
                    }),
                    loading ? /*#__PURE__*/ _jsx(GhostText, {
                        style: {
                            marginLeft: '20px',
                            width: 'inherit'
                        }
                    }) : /*#__PURE__*/ _jsx("div", {
                        style: textStyle,
                        className: clsx(S.textArea, limitText && txtOverflow && !hidden && S.hidden),
                        children: /*#__PURE__*/ _jsx("div", {
                            ref: contentRef,
                            style: {
                                fontSize: fontSize
                            },
                            children: allPages[currentIndex]?.text.split('\n').map((line, index)=>/*#__PURE__*/ _jsx("div", {
                                    style: {
                                        minHeight: '1em'
                                    },
                                    children: line
                                }, index))
                        }, currentIndex)
                    }),
                    !loading && /*#__PURE__*/ _jsx("div", {
                        className: S.actions,
                        children: txtOverflow && buildMore()
                    })
                ]
            }),
            modalOpen && /*#__PURE__*/ _jsx(Modal, {
                mobile: false,
                open: modalOpen,
                onClose: modalClose,
                imageId: allPages[currentIndex]?.otid,
                children: modalContent
            })
        ]
    });
};
